import React from 'react';

interface AsyncState<T, TError extends Error> {
  loading: boolean;
  error: TError | null;
  data: T | undefined;
}

interface Config<T, TError extends Error> {
  immediate: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: TError) => void;
}

export function useAsync<T, TError extends Error>(
  request: () => Promise<T>,
  { immediate, onError, onSuccess }: Config<T, TError> = { immediate: false }
): AsyncState<T, TError> & { execute: () => void } {
  // setting loading to immediate to prevent loading to be false during the first render
  const [state, setState] = React.useState<AsyncState<T, TError>>({ loading: immediate, error: null, data: undefined });

  const execute = React.useCallback(async () => {
    setState(prev => ({ loading: true, error: null, data: undefined }));
    try {
      const data = await request();
      setState(prev => ({ ...prev, loading: false, data }));
      onSuccess && onSuccess(data);
    } catch (e) {
      setState(prev => ({ ...prev, loading: false, error: e as TError }));
      onError && onError(e as TError);
    }
  }, [request, setState, onSuccess, onError]);

  React.useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);

  return {
    ...state,
    execute,
  };
}
